import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/axios';

export function useAnalyses(filters = {}) {
  return useQuery({
    queryKey: ['analyses', filters],
    queryFn: async () => {
      const { data } = await api.get('/analyses', { params: filters });
      return data;
    },
  });
}

export function useAnalysis(id) {
  return useQuery({
    queryKey: ['analyses', id],
    queryFn: async () => {
      const { data } = await api.get(`/analyses/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useAnalysisStatus(id) {
  return useQuery({
    queryKey: ['analyses', id, 'status'],
    queryFn: async () => {
      const { data } = await api.get(`/analyses/${id}/status`);
      return data;
    },
    enabled: !!id,
    refetchInterval: (data) => {
      if (data?.status === 'done' || data?.status === 'failed') return false;
      return 2000;
    },
  });
}

export function useDeleteAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.delete(`/analyses/${id}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analyses'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useStartAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post('/analyses', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analyses'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useUploadAnalysisAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, file, type, caption, note, section, measure_nr }) => {
      const fd = new FormData();
      if (file) fd.append('file', file);
      if (type) fd.append('type', type);
      if (caption) fd.append('caption', caption);
      if (note) fd.append('note', note);
      if (section) fd.append('section', section);
      if (measure_nr != null) fd.append('measure_nr', measure_nr);
      const { data } = await api.post(`/analyses/${analysisId}/assets`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analyses', vars.analysisId] });
    },
  });
}

export function useUpdateFoundations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, foundations }) => {
      const { data } = await api.patch(`/analyses/${analysisId}/foundations`, { foundations });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analyses', vars.analysisId] });
    },
  });
}

export function useUpdateTopMeasures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, top_measure_nrs }) => {
      const { data } = await api.patch(`/analyses/${analysisId}/top-measures`, { top_measure_nrs });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analyses', vars.analysisId] });
    },
  });
}

export function useDeleteAnalysisAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, assetId }) => {
      await api.delete(`/analyses/${analysisId}/assets/${assetId}`);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analyses', vars.analysisId] });
    },
  });
}

export function useUpdateAnalysisAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, assetId, payload }) => {
      const { data } = await api.patch(`/analyses/${analysisId}/assets/${assetId}`, payload);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analyses', vars.analysisId] });
    },
  });
}

export function useDispatchAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post(`/analyses/${id}/dispatch`);
      return data;
    },
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['analyses', id] });
      qc.invalidateQueries({ queryKey: ['analyses', id, 'status'] });
      qc.invalidateQueries({ queryKey: ['analyses'] });
    },
  });
}

export function useCreateManualMeasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, payload }) => {
      const { data } = await api.post(`/analyses/${analysisId}/measures/manual`, payload);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analyses', vars.analysisId] });
      qc.invalidateQueries({ queryKey: ['cro-measures-aggregate'] });
    },
  });
}

export function useUpdateManualMeasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, nr, payload }) => {
      const { data } = await api.patch(`/analyses/${analysisId}/measures/manual/${nr}`, payload);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analyses', vars.analysisId] });
      qc.invalidateQueries({ queryKey: ['cro-measures-aggregate'] });
    },
  });
}

export function useUpdateWireframeUrl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, wireframe_url }) => {
      const { data } = await api.patch(`/analyses/${analysisId}/wireframe-url`, { wireframe_url });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analyses', vars.analysisId] });
    },
  });
}

export function useDeleteManualMeasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ analysisId, nr }) => {
      await api.delete(`/analyses/${analysisId}/measures/manual/${nr}`);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['analyses', vars.analysisId] });
      qc.invalidateQueries({ queryKey: ['cro-measures-aggregate'] });
    },
  });
}

export function useSplitAnalysisToCluster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, title, auto_synthesize, delete_original }) => {
      const { data } = await api.post(`/analyses/${id}/split-to-cluster`, {
        title, auto_synthesize, delete_original,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analyses'] });
      qc.invalidateQueries({ queryKey: ['analysis-clusters'] });
    },
  });
}
